// The cost screens' state, as URL search params.
//
// Same hardened shape `closeSearch.ts` settled on, for the same reasons: every
// field carries its own `.catch(undefined)` so an unreadable param falls back
// rather than 404s, the parse runs AGAIN at the point of use (TanStack merges
// each route's validated search over its parent's, and the root validates
// nothing), and a default is written as no param at all.
//
// It matters more here than anywhere else in the app: "look at Kingsport's
// September with nonprod on" is a link, and a screen whose chips live in
// component state means that sentence cannot be sent to anybody.

import { z } from "zod";
import type { UsageTrendGroupBy } from "@crm/shared";
import {
  addMonths,
  formatIsoMonth,
  parseCalendarMonth,
  todayIsoDate,
} from "../../format/calendarDate.js";

const IsoMonthPattern = /^\d{4}-\d{2}$/;

/**
 * The dimensions the "Break down by" control offers.
 *
 * `day` and `deployment_slug` are deliberately not among them: the day IS the
 * chart above the list, and the deployment is the chip row above that. Offering
 * either here would give a reader two controls for one thing.
 */
export const USAGE_BREAKDOWNS = [
  "category",
  "product",
  "provider",
  "operation",
  "feature",
  "model",
] as const;

export type UsageBreakdown = (typeof USAGE_BREAKDOWNS)[number];

const UsageBreakdownSchema = z.enum(USAGE_BREAKDOWNS);

export const USAGE_COMPARISONS = [
  "tenant_slug",
  "category",
  "product",
  "provider",
  "model",
  "deployment_slug",
] as const;

export type UsageComparison = (typeof USAGE_COMPARISONS)[number];

const UsageComparisonSchema = z.enum(USAGE_COMPARISONS);

/** The ranges the trend offers. Twelve is the operation's ceiling and the axis's. */
export const TREND_MONTH_COUNTS = [3, 6, 12] as const;

export type TrendMonthCount = (typeof TREND_MONTH_COUNTS)[number];

const UsageSearchSchema = z.object({
  /** `YYYY-MM` — the month in view. Absent means the month we are in. */
  period: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  /** What the bar list is grouped by. Absent means category. */
  group: UsageBreakdownSchema.optional().catch(undefined),
  /** Comma-joined slugs. Absent means "whatever this browser last chose for this deal". */
  deployments: z.string().optional().catch(undefined),
  /** Comma-joined slugs. Absent means every tenant the deal binds. */
  tenants: z.string().optional().catch(undefined),
  /**
   * `/usage` only: the deployment whose tenants the product table splits.
   * Absent means the costliest production deployment.
   */
  tenantsOn: z.string().optional().catch(undefined),
});

export type UsageSearch = z.infer<typeof UsageSearchSchema>;

export function validateUsageSearch(raw: Record<string, unknown>): UsageSearch {
  const parsed = UsageSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

const UsageTrendSearchSchema = z.object({
  /** The LAST month of the range. Absent means the month we are in. */
  period: z.string().regex(IsoMonthPattern).optional().catch(undefined),
  months: z.coerce.number().int().optional().catch(undefined),
  compare: UsageComparisonSchema.optional().catch(undefined),
  deployments: z.string().optional().catch(undefined),
  tenants: z.string().optional().catch(undefined),
});

export type UsageTrendSearch = z.infer<typeof UsageTrendSearchSchema>;

export function validateUsageTrendSearch(raw: Record<string, unknown>): UsageTrendSearch {
  const parsed = UsageTrendSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/**
 * The month the cost screens open on: the one we are in.
 *
 * Not last month, which is what the close screen opens on. The close is work
 * that starts when a month ends; "what is this costing" is a question about the
 * month in flight, and it is the month the wireframe in section 3.1 draws.
 */
export function defaultUsagePeriod(today: string = todayIsoDate()): string {
  return today.slice(0, 7);
}

export function searchToUsagePeriod(
  search: { period?: string | undefined },
  today: string = todayIsoDate(),
): string {
  const month = search.period === undefined ? null : parseCalendarMonth(search.period);
  return month === null ? defaultUsagePeriod(today) : formatIsoMonth(month);
}

/** The default is written as no param, so a bare link keeps meaning "this month" tomorrow. */
export function periodToUsageSearch(
  period: string,
  today: string = todayIsoDate(),
): string | undefined {
  return period === defaultUsagePeriod(today) ? undefined : period;
}

export function searchToBreakdown(search: { group?: UsageBreakdown | undefined }): UsageBreakdown {
  return search.group ?? "category";
}

export function searchToComparison(search: {
  compare?: UsageComparison | undefined;
}): UsageTrendGroupBy {
  return search.compare ?? "tenant_slug";
}

export function searchToMonthCount(search: { months?: number | undefined }): TrendMonthCount {
  const found = TREND_MONTH_COUNTS.find((count) => count === search.months);
  return found ?? 6;
}

/** The first month of a range that ends at `period` and runs `count` months. */
export function rangeStart(period: string, count: number): string {
  const month = parseCalendarMonth(period);
  if (month === null) return period;
  return formatIsoMonth(addMonths(month, -(count - 1)));
}

/** Step a month at a time, which is what the `‹ ›` control does. */
export function shiftUsagePeriod(period: string, delta: number): string {
  const month = parseCalendarMonth(period);
  if (month === null) return period;
  return formatIsoMonth(addMonths(month, delta));
}

/** `a,b` in the URL, and nothing at all when the list is empty. */
export function slugsToSearch(slugs: readonly string[]): string | undefined {
  return slugs.length === 0 ? undefined : [...slugs].join(",");
}

/** The slugs a link carries, or undefined when it carries none. */
export function searchToSlugs(raw: string | undefined): string[] | undefined {
  if (raw === undefined) return undefined;
  const slugs = raw
    .split(",")
    .map((slug) => slug.trim())
    .filter((slug) => slug !== "");
  return slugs.length === 0 ? undefined : slugs;
}
